/**
 * Human labels for the audited entity types — the single source of truth
 * shared by the API (the "Deleted <type>" fallback in the audit label
 * resolver) and the web app (the audit log's object-type filter and Object
 * column). Keep the keys in lockstep with the server's `AUDITED_ENTITY_TYPES`
 * (apps/api/src/core/domains.ts); an API test asserts the two never drift.
 * Order drives the web dropdown order.
 */
export const AUDIT_ENTITY_TYPE_LABELS: Record<string, string> = {
  announcements: 'Announcement',
  approval_policies: 'Approval policy',
  invoice_settings: 'Invoice settings',
  invoice_attachments: 'Invoice attachment',
  customers: 'Customer',
  custom_fields: 'Custom field',
  customer_budgets: 'Customer budget',
  customer_contacts: 'Customer contact',
  customer_expenses: 'Customer expense',
  customer_variable_expense_occurrences: 'Variable expense amount',
  customer_team_members: 'Customer team member',
  dashboards: 'Dashboard',
  departments: 'Department',
  expense_catalogs: 'Expense item',
  holidays: 'Holiday',
  invoices: 'Invoice',
  products: 'Activity',
  ratecards: 'Rate card',
  release_notes: 'Release note',
  // "Report" and not "Report definition": to a reader of the audit log there
  // is no other kind of report to disambiguate from, and the word "definition"
  // is an implementation detail of how one is stored.
  report_definitions: 'Report',
  // Only STANDARD labels are audited — see `report_labels/service.ts`.
  report_labels: 'Report label',
  sows: 'Engagement',
  system_roles: 'System role',
  service_roles: 'Service role',
  skills: 'Skill',
  users: 'Employee',
  user_employee_costs: 'Employee cost',
  timesheets: 'Timesheet',
  timesheet_timers: 'Timer',
  timesheet_settings: 'Timesheet settings',
  timesheet_unlocks: 'Timesheet unlock',
};

/** Display label for an entity type; falls back to the raw type when unknown. */
export function entityTypeLabel(entityType: string): string {
  return AUDIT_ENTITY_TYPE_LABELS[entityType] ?? entityType;
}
